import { useEffect, useState } from 'react';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';

// Module-level cache, path -> resolved URL. Download URLs for a given object
// are stable, so a path resolved once never needs resolving again for the
// lifetime of the session. Lists that mount the same rows repeatedly (tab
// switches, re-renders) therefore issue zero extra requests, and a row that
// has been seen once paints its image with no loading state at all.
const urlCache = new Map<string, string>();

/**
 * Resolves a Firebase Storage path to a download URL asynchronously.
 * Returns null while loading or if the path is null / the fetch fails.
 * Already-resolved paths return synchronously from cache on first render.
 */
export function useStorageDownloadUrl(path: string | null): string | null {
  // Read the cache during render, not in an effect: a path already resolved
  // returns its URL on the very first render, with no request and no null
  // frame in between.
  const cached = path ? urlCache.get(path) ?? null : null;
  const [resolved, setResolved] = useState<{ path: string; url: string } | null>(null);

  useEffect(() => {
    if (!path || urlCache.has(path)) return;
    let cancelled = false;
    getDownloadURL(ref(storage, path))
      .then((u) => { urlCache.set(path, u); if (!cancelled) setResolved({ path, url: u }); })
      .catch(() => { /* silently fall back to placeholder */ });
    return () => { cancelled = true; };
  }, [path]);

  // Guard on path so a changed path never briefly shows the previous URL.
  return cached ?? (resolved && resolved.path === path ? resolved.url : null);
}
